/** Routes saved lineage separately; a failed saved read never becomes a source-intake journey. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../i18n/language.js';
import { Button, Card, PageHead, Tag } from '../../merchant/ui/primitives.js';
import { useWorkspace } from '../../merchant/shell-context.js';
import { workspaceCopy } from '../../merchant/copy.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import { ProductionLineDetailPage } from './ProductionLineDetailPage.js';
import { ProductionLineEditPage } from './ProductionLineEditPage.js';
import { SavedBriefForm } from './SavedBriefForm.js';
import { SavedBriefDrawer } from './SavedBriefDrawer.js';
import { SavedScriptReview } from './SavedScriptReview.js';
import { SavedIdeaCard } from './SavedIdeaCard.js';
import { FirstIdeaJourneyPage } from './FirstIdeaJourneyPage.js';
import { ResearchReferenceList } from './ResearchReferenceList.js';
import {
  ProductionLineMilestones,
  milestoneParam,
  MILESTONES,
} from './ProductionLineMilestones.js';
import {
  fetchSavedBrief,
  fetchBriefHistory,
  fetchSavedScript,
  selectSavedIdea,
  requestSavedScript,
  type SavedBriefJourney,
  type SavedBrief,
  type SavedScriptView,
} from './savedBriefApi.js';
const copy = {
  en: {
    loading: 'Loading the saved project…',
    failed:
      'This saved version could not be loaded. No earlier result has been substituted.',
    retry: 'Reload',
    lines: 'My projects',
    brief: 'View brief',
    edit: 'Edit brief',
    duplicate: 'Duplicate',
    history: 'Historical version — read only',
    current: 'View current version',
    researching: 'Research in progress',
    researchFailed: 'Research needs attention. Your brief is saved.',
    profile: 'Approved Business Profile',
    inherited:
      'This version uses the approved Business Profile shown below. It does not run business analysis again.',
    ideas: 'Choose an idea',
    select: 'Select idea',
    selected: 'Selected',
    script: 'Prepare script',
    busy: 'Saving…',
    changed:
      'Could not complete the action. Your selection is kept. Reload to check the latest saved state.',
    live: 'New reference',
    saved: 'Saved reference',
    business: 'Based on your business',
    complete: 'Research complete',
    relevant: 'Why it fits',
    treatment: 'Proposed treatment',
    metrics: 'Metrics describe the original content, not a promise of results.',
    production:
      'Script approved. Production is not configured for this saved-script path. No video has been generated or charged.',
    researchHint:
      'The worker saves progress and recovery state. This page only reads it.',
  },
  ar: {
    loading: 'جارٍ تحميل المشروع المحفوظ…',
    failed: 'تعذّر تحميل هذا الإصدار. لم نستبدله بنتيجة سابقة.',
    retry: 'إعادة التحميل',
    lines: 'مشاريعي',
    brief: 'عرض الملخص',
    edit: 'تعديل الملخص',
    duplicate: 'نسخ',
    history: 'إصدار سابق — للقراءة فقط',
    current: 'عرض الإصدار الحالي',
    researching: 'البحث جارٍ',
    researchFailed: 'البحث يحتاج إلى متابعة. ملخصك محفوظ.',
    profile: 'الملف التجاري المعتمد',
    inherited:
      'يستخدم هذا الإصدار الملف التجاري المعتمد أدناه، دون إعادة تحليل النشاط.',
    ideas: 'اختر فكرة',
    select: 'اختيار الفكرة',
    selected: 'مختارة',
    script: 'إعداد النص',
    busy: 'جارٍ الحفظ…',
    changed: 'تعذّر إكمال الإجراء. اختيارك محفوظ. أعد التحميل للتحقق من أحدث حالة.',
    live: 'مرجع جديد',
    saved: 'مرجع محفوظ',
    business: 'مبنية على نشاطك',
    complete: 'اكتمل البحث',
    relevant: 'لماذا تناسبك',
    treatment: 'المعالجة المقترحة',
    metrics: 'الأرقام تصف المحتوى الأصلي ولا تضمن النتائج.',
    production:
      'تم اعتماد النص. إنتاج الفيديو غير مهيّأ لمسار النص المحفوظ. لم يتم إنشاء فيديو أو احتساب تكلفته.',
    researchHint: 'يحفظ النظام تقدّم العمل وحالة الاستعادة. هذه الصفحة تعرضها فقط.',
  },
};
export function SavedProductionLinePage({ editing = false }: { editing?: boolean }) {
  const { productionLineId = '' } = useParams(),
    [params] = useSearchParams(),
    { language } = useLanguage();
  const version = params.get('version') ?? undefined,
    ui = copy[language];
  const ideaRevision = params.get('ideaRevision') ?? undefined;
  const journeyStart = params.get('journey');
  const [result, setResult] = useState<Awaited<
    ReturnType<typeof fetchSavedBrief>
  > | null>(null);
  const [failed, setFailed] = useState(false),
    [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setResult(null);
    setFailed(false);
    void fetchSavedBrief(productionLineId, version, controller.signal, ideaRevision)
      .then((r) => {
        if (!controller.signal.aborted) setResult(r);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => {
      controller.abort();
    };
  }, [productionLineId, version, ideaRevision, journeyStart, retry]);
  if (failed)
    return (
      <Card>
        <p role="alert">{ui.failed}</p>
        <Button
          onClick={() => {
            setRetry((v) => v + 1);
          }}
        >
          {ui.retry}
        </Button>
      </Card>
    );
  if (result === null) return <p role="status">{ui.loading}</p>;
  if (result.kind === 'LEGACY')
    return editing ? <ProductionLineEditPage /> : <ProductionLineDetailPage />;
  if (result.firstIdea?.schemaVersion === 'first-idea-journey.v1')
    return (
      <FirstIdeaJourneyPage
        key={`${productionLineId}:${version ?? 'current'}:${ideaRevision ?? 'current'}`}
        initial={result}
      />
    );
  return (
    <SavedJourney
      key={`${productionLineId}:${version ?? 'current'}`}
      initial={result}
      editing={editing}
    />
  );
}
function SavedJourney({
  initial,
  editing,
}: {
  initial: SavedBriefJourney;
  editing: boolean;
}) {
  const { language } = useLanguage(),
    ui = copy[language],
    wsCopy = workspaceCopy(language);
  const navigate = useNavigate(),
    workspace = useWorkspace(),
    [params, setParams] = useSearchParams();
  const { refresh: refreshWorkspace } = workspace;
  const [data, setData] = useState(initial),
    [script, setScript] = useState<SavedScriptView | null>(null);
  const [versions, setVersions] = useState<SavedBrief[]>([]),
    [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState<'edit' | 'duplicate' | null>(
    editing ? 'edit' : null,
  );
  const [selected, setSelected] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [failed, setFailed] = useState(false);
  const alive = useRef(true),
    reading = useRef(false);
  const intent = useRef<{
    id: string;
    selectKey: string;
    scriptKey: string;
    selectionId?: string;
  } | null>(null);
  const version = params.get('version') ?? undefined;
  const refresh = useCallback(async () => {
    if (reading.current) return;
    reading.current = true;
    try {
      const next = await fetchSavedBrief(initial.productionLineId, version);
      if (next.kind !== 'SAVED_BRIEF') throw new Error('Saved brief unavailable');
      const output = next.script ? await fetchSavedScript(next.script.publicId) : null;
      if (alive.current) {
        setData(next);
        setScript(output);
        setFailed(false);
      }
    } catch {
      if (alive.current) setFailed(true);
    } finally {
      reading.current = false;
    }
  }, [initial.productionLineId, version]);
  useEffect(() => {
    alive.current = true;
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => {
      alive.current = false;
      window.clearInterval(timer);
    };
  }, [refresh]);
  useEffect(() => {
    // Match first-idea journeys: observing a saved script transition also
    // refreshes the shared list/sidebar without dispatching another command.
    void refreshWorkspace();
  }, [refreshWorkspace, data.brief.currentStage, script?.state]);
  const researchDone =
    data.brief.currentStage === 'COMPLETE' && data.brief.executionState === 'SUCCEEDED';
  const current = script?.approval ? 'production' : researchDone ? 'idea' : 'research';
  const requestedStep = milestoneParam(params.get('step'));
  const viewed =
    requestedStep && MILESTONES.indexOf(requestedStep) <= MILESTONES.indexOf(current)
      ? requestedStep
      : current;
  const references = data.ideas.directions.map((d) => ({
    artifactId: d.artifactId,
    title: d.payload.title,
    basis: d.payload.contentIdea.basis,
    references: d.payload.contentIdea.references,
  }));
  async function prepare() {
    if (!selected || busy || !data.isCurrent) return;
    const id = `${data.brief.versionId}:${selected}`;
    if (intent.current?.id !== id)
      intent.current = {
        id,
        selectKey: newIdempotencyKey(),
        scriptKey: newIdempotencyKey(),
      };
    const attempt = intent.current;
    setBusy(true);
    setFailed(false);
    try {
      if (!attempt.selectionId)
        attempt.selectionId = (
          await selectSavedIdea(
            {
              productionLineId: data.productionLineId,
              briefVersionId: data.brief.versionId,
              directionArtifactId: selected,
              handoffArtifactId: data.ideas.handoffArtifactId,
            },
            attempt.selectKey,
          )
        ).selectionId;
      await requestSavedScript(attempt.selectionId, attempt.scriptKey);
      await refresh();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }
  async function openBrief() {
    try {
      const history = await fetchBriefHistory(data.productionLineId);
      setVersions(history.versions);
      setDrawer(true);
    } catch {
      setFailed(true);
    }
  }
  return (
    <div className="mj-ws-page mj-ws-saved-journey" data-workspace-design="v1.12">
      <Link to="/production-lines">{ui.lines}</Link>
      <PageHead
        title={data.brief.title}
        titleId="saved-line-title"
        action={
          <div className="mj-ws-row">
            <Button variant="secondary" onClick={() => void openBrief()}>
              {ui.brief}
            </Button>
            <Button
              variant="secondary"
              disabled={!data.canEdit || busy}
              onClick={() => {
                setForm('edit');
              }}
            >
              {ui.edit}
            </Button>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setForm('duplicate');
              }}
            >
              {ui.duplicate}
            </Button>
          </div>
        }
      />
      {!data.isCurrent && (
        <p role="status">
          {ui.history} ·{' '}
          <Link to={`/production-lines/${data.productionLineId}`}>{ui.current}</Link>
        </p>
      )}
      <ProductionLineMilestones
        current={current}
        viewed={viewed}
        lineId={data.productionLineId}
        language={language}
        {...(version ? { versionId: version } : {})}
      />
      {failed && (
        <Card>
          <p role="alert">{ui.changed}</p>
          <Button onClick={() => void refresh()}>{ui.retry}</Button>
        </Card>
      )}
      {viewed === 'business' && (
        <Card>
          <h2>{ui.profile}</h2>
          <p>{ui.inherited}</p>
          <dl className="mj-ws-rdl mj-ws-saved-profile">
            {[
              [wsCopy.profile.name, data.sourceProfile.payload.identity.value],
              [wsCopy.profile.description, data.sourceProfile.payload.niche.value],
              [wsCopy.profile.geography, data.sourceProfile.payload.geography.value],
              ...data.sourceProfile.payload.offers.map((o) => [
                o.title.value,
                o.description.value,
              ]),
            ].map(([label, value], i) => (
              <div key={i}>
                <dt dir="auto">{label ?? wsCopy.common.notEstablished}</dt>
                <dd dir="auto">{value ?? wsCopy.common.notEstablished}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}
      {viewed === 'research' && (
        <Card>
          <h2 className="mj-ws-h2">
            {researchDone
              ? ui.complete
              : data.brief.queueState === 'FAILED'
                ? ui.researchFailed
                : ui.researching}
          </h2>
          <p>{researchDone ? ui.metrics : ui.researchHint}</p>
          <ResearchReferenceList ideas={references} language={language} copy={wsCopy} />
        </Card>
      )}
      {viewed === 'idea' && (
        <>
          {script?.selectedIdea && (
            <Card>
              <Tag>{ui.selected}</Tag>
              <h3 dir="auto" lang={script.selectedIdea.outputLocale}>
                {script.selectedIdea.title}
              </h3>
              <p dir="auto">{script.selectedIdea.hook}</p>
            </Card>
          )}
          {script ? (
            <SavedScriptReview
              view={script}
              language={language}
              readOnly={!data.isCurrent || failed}
              onRefresh={refresh}
            />
          ) : (
            <>
              <h2 className="mj-ws-h2">{ui.ideas}</h2>
              <div
                className="mj-ws-g3 mj-ws-mt-20"
                role="radiogroup"
                aria-label={ui.ideas}
              >
                {data.ideas.directions.map((d) => (
                  <SavedIdeaCard
                    key={d.artifactId}
                    id={d.artifactId}
                    title={d.payload.title}
                    selected={selected === d.artifactId}
                    disabled={!data.isCurrent || busy}
                    onSelect={() => {
                      setSelected(d.artifactId);
                    }}
                  >
                    <Tag>
                      {d.payload.contentIdea.basis === 'BUSINESS_BASED'
                        ? ui.business
                        : d.payload.contentIdea.basis === 'SAVED_REFERENCE'
                          ? ui.saved
                          : ui.live}
                    </Tag>
                    <h3 className="mj-ws-h3" dir="auto">
                      {d.payload.title}
                    </h3>
                    <p dir="auto">{d.payload.contentIdea.hook}</p>
                    <p dir="auto">{d.payload.angle}</p>
                    <details>
                      <summary>{ui.relevant}</summary>
                      <p dir="auto">{d.payload.contentIdea.whyRelevant}</p>
                      <h4>{ui.treatment}</h4>
                      <p dir="auto">{d.payload.contentIdea.creativeTreatment}</p>
                    </details>
                  </SavedIdeaCard>
                ))}
              </div>
              <Button
                variant="primary"
                className="mj-ws-script-prepare"
                pixelWave
                disabled={
                  !selected || busy || !data.isCurrent || !data.ideas.handoffArtifactId
                }
                aria-busy={busy}
                onClick={() => void prepare()}
              >
                {busy ? ui.busy : ui.script}
              </Button>
            </>
          )}
          <ResearchReferenceList ideas={references} language={language} copy={wsCopy} />
        </>
      )}
      {viewed === 'production' && (
        <Card>
          <p role="status">{ui.production}</p>
        </Card>
      )}
      {drawer && (
        <SavedBriefDrawer
          brief={data.brief}
          versions={versions}
          currentVersionId={data.currentVersionId}
          language={language}
          onClose={() => {
            setDrawer(false);
          }}
          onVersion={(id) => {
            setDrawer(false);
            setParams({ version: id });
          }}
        />
      )}
      {form && (
        <SavedBriefForm
          mode={form}
          existing={data.brief}
          onClose={() => {
            setForm(null);
            if (editing) void navigate(`/production-lines/${data.productionLineId}`);
          }}
          onCreated={(lineId) => {
            setForm(null);
            void workspace.refresh();
            void navigate(`/production-lines/${lineId}`);
            void refresh();
          }}
        />
      )}
    </div>
  );
}
